"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { usePixelatedConfig } from "../config/config.client";
import { SmartImage } from '../elements/smartimage';
import "./callout.scss";
/* ==================== NOTES ====================
DEFAULT = flexbox layout, no border around callout
BOXED = border around callout
BOXED GRID = applies both
FULL = full width callout with minimal margin and padding
GRID = grid layout
SPLIT = full width split page layout, cannot use LAYOUT (HORIZONTAL / VERTICAL)
LAYOUT = horizontal or vertical callout, VERTICAL cannot use DIRECTION (LEFT / RIGHT)
DIRECTION = used to place image on left or right side, does not apply to VERTICAL layout

GRID is basic 1/2 GRID shape, needs enhanement
BOXSHAPE has not been complete
==================== NOTES ==================== */
// Define option arrays - used by both PropTypes and form generation
export const variants = ['default', 'boxed', 'boxed grid', 'full', 'grid', 'overlay', 'split'];
export const shapes = ['square', 'bevel', 'squircle', 'round'];
export const layouts = ['horizontal', 'vertical'];
export const directions = ['left', 'right'];
/**
 * Callout — versatile content block used for banners, promotional sections, and feature callouts.
 *
 * @param {oneOf} [props.variant] - Visual variant (e.g., 'default', 'boxed', 'full', 'grid', 'overlay', 'split').
 * @param {oneOf} [props.boxShape] - Corner shape for boxed variants (e.g., 'square', 'bevel', 'squircle', 'round').
 * @param {oneOf} [props.layout] - Layout direction: 'horizontal' (image + content) or 'vertical'.
 * @param {oneOf} [props.direction] - When horizontal, whether the image appears on the 'left' or 'right'.
 * @param {shape} [props.gridColumns] - Optional grid column counts used for boxed grid variants ({ left, right }).
 * @param {number} [props.left] - Left column width for grid layouts.
 * @param {number} [props.right] - Right column width for grid layouts.
 * @param {string} [props.url] - Optional CTA URL for the callout button.
 * @param {string} [props.img] - Image URL to display in the callout.
 * @param {string} [props.imgAlt] - Alternate text for the image (accessibility and caption fallback).
 * @param {oneOf} [props.imgShape] - Visual shape applied to the image container.
 * @param {function} [props.imgClick] - Optional image click handler (event, url).
 * @param {string} [props.title] - Primary title text for the callout.
 * @param {string} [props.subtitle] - Secondary title/subtitle text.
 * @param {oneOfType} [props.content] - Content as string or React node to place inside the callout body.
 * @param {node} [props.children] - React children to render in the content area (takes precedence over content).
 * @param {string} [props.buttonText] - Button/CTA text to display when `url` is provided.
 * @param {boolean} [props.aboveFold] - Hint to prioritize image loading (above-the-fold) for performance.
 */
Callout.propTypes = {
    /** Visual variant selector (e.g., 'default', 'boxed', 'grid', 'full'). */
    variant: PropTypes.oneOf([...variants]),
    /** Corner/box shape for boxed variants. */
    boxShape: PropTypes.oneOf([...shapes]),
    /** Layout direction: 'horizontal' or 'vertical'. */
    layout: PropTypes.oneOf([...layouts]),
    /** When horizontal layout is used, position image on 'left' or 'right'. */
    direction: PropTypes.oneOf([...directions]),
    /** Grid column sizing used for 'boxed grid' variants. */
    gridColumns: PropTypes.shape({
        /** Left column width in the grid configuration. */
        left: PropTypes.number,
        /** Right column width in the grid configuration. */
        right: PropTypes.number
    }),
    /** Optional CTA URL used by CalloutButton. */
    url: PropTypes.string,
    /** Image URL to display in the callout. */
    img: PropTypes.string,
    /** Alt text for the image (used for accessibility and caption fallback). */
    imgAlt: PropTypes.string,
    /** Visual shape applied to the image container (e.g., 'square', 'round'). */
    imgShape: PropTypes.oneOf([...shapes]),
    /** Optional click handler for the image (event, url). */
    imgClick: PropTypes.func,
    /** Primary title text for the callout. */
    title: PropTypes.string,
    /** Optional subtitle/secondary heading. */
    subtitle: PropTypes.string,
    /** Content string or React node to render inside the body area. */
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /** React children to render in the content area (takes precedence over content prop). */
    children: PropTypes.node,
    /** Button or CTA text used when `url` is provided. */
    buttonText: PropTypes.string,
    // SmartImage props
    /** Hint to prioritize image loading (treat as above-the-fold). */
    aboveFold: PropTypes.bool,
};
export function Callout({ variant = 'default', boxShape = "squircle", layout = "horizontal", direction = 'left', gridColumns = { left: 1, right: 2 }, url, img, imgAlt, imgShape = 'square', imgClick, title, subtitle, content, children, buttonText, aboveFold, }) {
    const target = url && url.substring(0, 4).toLowerCase() === 'http' ? '_blank' : '_self';
    const friendlyTitle = title ? title.toLowerCase().replace(/\s+/g, '-') : undefined;
    const body = _jsxs("div", { className: "callout-body", children: [(title) ? _jsx(CalloutHeader, { title: title, url: url, target: target }) : null, (subtitle) ? _jsx("div", { className: "callout-subtitle", children: _jsx("h3", { children: subtitle }) }) : null, children ? _jsx("div", { className: "callout-content", children: children }) : content ? _jsx("div", { className: "callout-content", children: _jsx(_Fragment, { children: content }) }) : null, url && buttonText
                ? _jsx(CalloutButton, { title: buttonText, url: url, target: target })
                : url && title
                    ? _jsx(CalloutButton, { title: title || "", url: url, target: target })
                    : null] });
    const config = usePixelatedConfig();
    const image = (img) ?
        _jsx("div", { className: "callout-image" + (imgShape ? " " + imgShape : ""), children: (url && !imgClick)
                ? _jsx("a", { href: url, target: target, rel: target == "_blank" ? "noopener noreferrer" : "", children: _jsx(SmartImage, { src: img, title: title ?? imgAlt ?? undefined, alt: imgAlt ?? title ?? "", aboveFold: aboveFold, cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined, suppressHydrationWarning: true }) })
                : (url && imgClick)
                    ? _jsx(SmartImage, { src: img, title: title ?? imgAlt ?? undefined, alt: imgAlt ?? title ?? "", onClick: (event) => imgClick(event, url), aboveFold: aboveFold, cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined, suppressHydrationWarning: true })
                    : _jsx(SmartImage, { src: img, title: title ?? imgAlt ?? undefined, alt: imgAlt ?? title ?? "", aboveFold: aboveFold, cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined, suppressHydrationWarning: true }) }) : null;
    return (_jsx("div", { id: friendlyTitle ? "callout-" + friendlyTitle : undefined, className: "callout" +
            (variant ? " " + variant : "") +
            ((variant === 'boxed' || variant === 'boxed grid') && boxShape ? " " + boxShape : "") +
            (layout && variant !== 'split' ? " " + layout : "") +
            (direction && layout !== 'vertical' ? " " + direction : "") +
            (variant && (variant === 'boxed grid' || variant === 'grid') && gridColumns ? ` callout-grid-${gridColumns.left}-${gridColumns.right}` : ''), suppressHydrationWarning: true, children: (direction === "right") ? _jsxs(_Fragment, { children: [body, image] }) : _jsxs(_Fragment, { children: [image, body] }) }));
}
/* ========== CALLOUT HEADER ========== */
/**
 * CalloutHeader — renders the primary title and optional link wrapper for a Callout.
 *
 * @param {string} [props.title] - Title text displayed as the heading.
 * @param {string} [props.url] - Optional URL that wraps the title as a link.
 * @param {string} [props.target] - Link target (e.g., '_self', '_blank').
 */
CalloutHeader.propTypes = {
    /** Heading title text (required). */
    title: PropTypes.string.isRequired,
    /** Optional link URL for the heading. */
    url: PropTypes.string,
    /** Optional link target attribute. */
    target: PropTypes.string
};
export function CalloutHeader({ title, url, target }) {
    return (_jsx("div", { className: "callout-header", children: (url)
            ? _jsx("a", { href: url, target: target ? target : "", rel: target == "_blank" ? "noopener noreferrer" : "", children: _jsx("h2", { className: "callout-title", children: title }) })
            : _jsx("h2", { className: "callout-title", children: title }) }));
}
/* ========== CALLOUT BUTTON ========== */
/**
 * CalloutButton — renders a button that navigates to a provided CTA URL.
 *
 * @param {string} [props.title] - Button label text (required when URL is provided).
 * @param {string} [props.url] - Required URL that the button navigates to.
 * @param {string} [props.target] - Optional link target for cross-origin behavior.
 */
CalloutButton.propTypes = {
    /** Button label text. */
    title: PropTypes.string.isRequired,
    /** CTA URL that the button will navigate to when clicked. */
    url: PropTypes.string.isRequired,
    /** Optional target attribute for link behavior. */
    target: PropTypes.string
};
export function CalloutButton({ title, url, target }) {
    const handleClick = () => {
        if (target === '_blank') {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        else {
            window.location.href = url;
        }
    };
    return (_jsx("div", { className: "callout-button", children: (url)
            ? _jsx("button", { type: "button", className: "callout-button", onClick: handleClick, children: title })
            : null }));
}
